import pedalConfig from "./pedal3d/pedal-config.js";
import { buildPedalGroup } from "./pedal3d/pedal.js";
import { OrbitControls } from "./vendor/OrbitControls.js";
import * as THREE from "./vendor/three.module.js";

const mount = document.getElementById("root");
if (!mount) {
  throw new Error("Missing #root mount for 3D pedal renderer.");
}

const debugEnabled = new URLSearchParams(window.location.search).has("pedaldebug");
const debugEl = document.getElementById("pedal-3d-debug");
const debugLinesEl = document.getElementById("pedal-3d-debug-lines");
const debugStatusEl = document.getElementById("pedal-3d-debug-status");
const debugLines = [];
function debugLog(message) {
  if (!debugEnabled || !debugEl || !debugLinesEl) return;
  debugEl.hidden = false;
  const now = new Date();
  const ts = `${String(now.getMinutes()).padStart(2, "0")}:${String(now.getSeconds()).padStart(2, "0")}.${String(now.getMilliseconds()).padStart(3, "0")}`;
  debugLines.push(`${ts} ${message}`);
  while (debugLines.length > 8) debugLines.shift();
  debugLinesEl.textContent = debugLines.join("\n");
}

let debugStatusLastMs = 0;
function fmt3(value) {
  return Number(value).toFixed(3);
}

function updateDebugStatus(nowMs) {
  if (!debugEnabled || !debugStatusEl) return;
  if (nowMs - debugStatusLastMs < 120) return;
  debugStatusLastMs = nowMs;
  const azDeg = THREE.MathUtils.radToDeg(controls.getAzimuthalAngle());
  const polDeg = THREE.MathUtils.radToDeg(controls.getPolarAngle());
  debugStatusEl.textContent =
    `cam:    x ${fmt3(camera.position.x)}  y ${fmt3(camera.position.y)}  z ${fmt3(camera.position.z)}\n` +
    `target: x ${fmt3(controls.target.x)}  y ${fmt3(controls.target.y)}  z ${fmt3(controls.target.z)}\n` +
    `orbit:  az ${fmt3(azDeg)}deg  pol ${fmt3(polDeg)}deg  dist ${fmt3(camera.position.distanceTo(controls.target))}`;
}

const scene = new THREE.Scene();

const renderer = new THREE.WebGLRenderer({ antialias: true, alpha: true });
renderer.setPixelRatio(Math.min(window.devicePixelRatio || 1, 2));
renderer.outputColorSpace = THREE.SRGBColorSpace;
renderer.setClearColor(0x000000, 0);
renderer.domElement.style.width = "100%";
renderer.domElement.style.height = "100%";
renderer.domElement.style.display = "block";
renderer.domElement.style.touchAction = "none";
mount.appendChild(renderer.domElement);

const camera = new THREE.PerspectiveCamera(45, 1, 0.1, 100);
camera.position.set(1.5, -0.115, 1.9);

const controls = new OrbitControls(camera, renderer.domElement);
controls.enablePan = false;
controls.enableZoom = false;
controls.minDistance = 0.8;
controls.maxDistance = 3;
controls.enableDamping = true;
controls.dampingFactor = 0.08;
controls.autoRotate = true;
controls.autoRotateSpeed = 0.35;
controls.target.set(0, 0, 0.16);
controls.update();

const isCoarsePointer = window.matchMedia?.("(pointer: coarse)")?.matches ?? false;
if (isCoarsePointer) {
  // Let vertical swipes scroll the page; keep horizontal drag for pedal spin.
  renderer.domElement.style.touchAction = "pan-y";
  const fixedPolar = controls.getPolarAngle();
  controls.minPolarAngle = fixedPolar;
  controls.maxPolarAngle = fixedPolar;

  // Mobile Safari can still trigger double-tap zoom on canvas; suppress only
  // the second tap default action inside this element.
  let lastTouchEndMs = 0;
  renderer.domElement.addEventListener(
    "touchend",
    (event) => {
      const nowMs = performance.now();
      if (nowMs - lastTouchEndMs < 320) {
        event.preventDefault();
      }
      lastTouchEndMs = nowMs;
    },
    { passive: false },
  );
}

scene.add(new THREE.AmbientLight(0xffffff, 0.65));
const keyLight = new THREE.DirectionalLight(0xffffff, 1.7);
keyLight.position.set(2, -4, 8);
scene.add(keyLight);
const fillLight = new THREE.DirectionalLight(0xaeb7c4, 0.8);
fillLight.position.set(-3, 2, 4);
scene.add(fillLight);

const screenprintTexture = new THREE.TextureLoader().load(
  pedalConfig.screenprint.texturePath,
);
screenprintTexture.colorSpace = THREE.SRGBColorSpace;

const textureLoader = new THREE.TextureLoader();
const loadSurfaceTexture = (url, isColor = false) => {
  const texture = textureLoader.load(
    url,
    () => debugLog(`tex ok ${url}`),
    undefined,
    () => debugLog(`tex FAIL ${url}`),
  );
  texture.wrapS = THREE.RepeatWrapping;
  texture.wrapT = THREE.RepeatWrapping;
  texture.repeat.set(1, 1);
  if (isColor) {
    texture.colorSpace = THREE.SRGBColorSpace;
  }
  texture.anisotropy = Math.min(8, renderer.capabilities.getMaxAnisotropy());
  return texture;
};

const bodyMaps = {
  colorMap: loadSurfaceTexture(
    "textures/metal044a/Metal044A_2K-PNG_Color.png",
    true,
  ),
  roughnessMap: loadSurfaceTexture(
    "textures/metal044a/Metal044A_2K-PNG_Roughness.png",
  ),
  metalnessMap: loadSurfaceTexture(
    "textures/metal044a/Metal044A_2K-PNG_Metalness.png",
  ),
  normalMap: loadSurfaceTexture(
    "textures/metal044a/Metal044A_2K-PNG_NormalGL.png",
  ),
};

const pedal = buildPedalGroup(pedalConfig, { screenprintTexture, bodyMaps });
scene.add(pedal);

const raycaster = new THREE.Raycaster();
const pointer = new THREE.Vector2(0, 0);

const clamp01 = (value) => Math.max(0, Math.min(1, value));
// 0% = ~7 o'clock, 100% = ~5 o'clock (clockwise sweep through the top arc).
const knobAngleFromValue = (value) => THREE.MathUtils.degToRad(-135 - clamp01(value) * 270);

const state = {
  wet: 0.75,
  dry: 0.75,
  decay: 1,
  lowShelfEnabled: true,
  tailRunOnBypass: true,
  freezeMuteInput: false,
  bypassEnabled: false,
  bypassPressed: false,
  freezePressed: false,
};

function getObject(name) {
  return pedal.getObjectByName(name);
}

function collectByPrefix(prefix) {
  const items = [];
  pedal.traverse((obj) => {
    if (obj?.name?.startsWith(prefix)) {
      items.push(obj);
    }
  });
  return items;
}

function firstMeshDescendant(root) {
  let found = null;
  root?.traverse?.((obj) => {
    if (!found && obj?.isMesh) found = obj;
  });
  return found;
}

function makeKnobRef(key, id) {
  const knob = getObject(`Knob-${id}`);
  const stripeTop = getObject(`KnobStripeTop-${id}`);
  const stripeSide = getObject(`KnobStripeSide-${id}`);
  if (!knob) return null;
  const pivot = new THREE.Group();
  pivot.name = `KnobPivot-${id}`;
  pivot.position.copy(knob.position);
  pedal.add(pivot);
  pivot.attach(knob);
  if (stripeTop) pivot.attach(stripeTop);
  if (stripeSide) pivot.attach(stripeSide);
  return { key, id, pivot, pickMesh: knob };
}

function makeSwitchRef(key, pivot) {
  if (!pivot) return null;
  const pickMesh = firstMeshDescendant(pivot);
  if (!pickMesh) return null;
  const id = String(pivot.name || "").replace("SwitchLeverPivot-", "");
  return { key, id, pivot, pickMesh };
}

function makeFootswitchRef(key, top) {
  if (!top) return null;
  const id = String(top.name || "").replace("FootswitchTop-", "");
  const shaft = getObject(`FootswitchShaft-${id}`);
  return { key, id, top, shaft, baseTopZ: top.position.z, baseShaftZ: shaft?.position.z ?? 0, pickMesh: top };
}

const knobKeys = ["decay", "wet", "dry"];
const switchKeys = ["tailRunOnBypass", "lowShelfEnabled", "freezeMuteInput"];

const knobMeshes = collectByPrefix("Knob-")
  .filter((obj) => obj.isMesh)
  .sort((a, b) => a.position.x - b.position.x);
const knobs = knobMeshes
  .slice(0, knobKeys.length)
  .map((mesh, index) => {
    const key = knobKeys[index];
    const id = mesh.name.replace("Knob-", "");
    const ref = makeKnobRef(key, id);
    if (ref) ref.pickMesh = mesh;
    return ref;
  })
  .filter(Boolean);

const switchPivots = collectByPrefix("SwitchLeverPivot-")
  .filter((obj) => obj.isObject3D)
  .sort((a, b) => a.position.x - b.position.x);
const switches = switchPivots
  .slice(0, switchKeys.length)
  .map((pivot, index) => {
    const key = switchKeys[index];
    return makeSwitchRef(key, pivot);
  })
  .filter(Boolean);

const toggleHitZones = switches.map((sw) => {
  const hit = new THREE.Mesh(
    new THREE.SphereGeometry(0.055, 12, 10),
    new THREE.MeshBasicMaterial({
      transparent: true,
      opacity: 0,
      depthWrite: false,
      depthTest: false,
      side: THREE.DoubleSide,
    }),
  );
  hit.name = `ToggleHit-${sw.id}`;
  hit.position.copy(sw.pivot.position);
  hit.position.z += 0.04;
  hit.userData.fpControl = { type: "toggle", key: sw.key };
  pedal.add(hit);
  return hit;
});

const footswitchTopMeshes = collectByPrefix("FootswitchTop-")
  .filter((obj) => obj.isMesh)
  .sort((a, b) => a.position.x - b.position.x);
const footswitches = footswitchTopMeshes
  .slice(0, 2)
  .map((mesh, index) => {
    const key = index === 0 ? "bypass" : "freeze";
    const ref = makeFootswitchRef(key, mesh);
    if (ref) ref.pickMesh = mesh;
    return ref;
  })
  .filter(Boolean);

if (knobs.length < 3 || switches.length < 3 || footswitches.length < 2) {
  console.warn(
    "[pedal3d] Interactive mesh discovery incomplete:",
    { knobs: knobs.length, switches: switches.length, footswitches: footswitches.length },
  );
}

const leds = {
  bypass: {},
  freeze: {},
};

const ledDomeMeshes = collectByPrefix("LED-")
  .filter((obj) => obj.isMesh)
  .sort((a, b) => a.position.x - b.position.x);
const ledBodyMeshes = collectByPrefix("LEDBody-")
  .filter((obj) => obj.isMesh)
  .sort((a, b) => a.position.x - b.position.x);
leds.bypass.body = ledBodyMeshes[0] ?? null;
leds.bypass.dome = ledDomeMeshes[0] ?? null;
leds.freeze.body = ledBodyMeshes[1] ?? null;
leds.freeze.dome = ledDomeMeshes[1] ?? null;

function setLedIntensity(mesh, intensity) {
  if (!mesh || !mesh.material) return;
  mesh.material.emissiveIntensity = intensity;
}

const downWhenTrueToggles = new Set(["lowShelfEnabled", "freezeMuteInput"]);

function switchTilt(key, enabled) {
  const isDown = downWhenTrueToggles.has(key) ? enabled : !enabled;
  return isDown ? 0.38 : -0.38;
}

function applyVisualState() {
  knobs.forEach((knob) => {
    const value = state[knob.key];
    knob.pivot.rotation.z = knobAngleFromValue(value);
  });

  switches.forEach((sw) => {
    sw.pivot.rotation.x = switchTilt(sw.key, Boolean(state[sw.key]));
  });

  footswitches.forEach((fs) => {
    const isPressed =
      (fs.key === "bypass" && state.bypassPressed) ||
      (fs.key === "freeze" && state.freezePressed);
    const pressDepth = isPressed ? 0.011 : 0;
    fs.top.position.z = fs.baseTopZ - pressDepth;
    if (fs.shaft) fs.shaft.position.z = fs.baseShaftZ - pressDepth * 0.25;
  });

  const bypassOn = !state.bypassEnabled;
  const freezeOn = !!state.freezePressed;
  setLedIntensity(leds.bypass.body, bypassOn ? 1.6 : 0);
  setLedIntensity(leds.bypass.dome, bypassOn ? 2.5 : 0);
  setLedIntensity(leds.freeze.body, freezeOn ? 1.6 : 0);
  setLedIntensity(leds.freeze.dome, freezeOn ? 2.5 : 0);
}

function emitControl(detail) {
  window.dispatchEvent(new CustomEvent("fp-3d-control", { detail }));
}

function setState(patch) {
  Object.assign(state, patch);
  applyVisualState();
}

window.familiarPedal3D = {
  setState,
  getState: () => ({ ...state }),
};

const interactiveMeshes = [];
toggleHitZones.forEach((hit) => {
  interactiveMeshes.push(hit);
});
knobs.forEach((knob) => {
  knob.pickMesh.userData.fpControl = { type: "knob", key: knob.key };
  interactiveMeshes.push(knob.pickMesh);
});
switches.forEach((sw) => {
  sw.pickMesh.userData.fpControl = { type: "toggle", key: sw.key };
  interactiveMeshes.push(sw.pickMesh);
});
footswitches.forEach((fs) => {
  fs.pickMesh.userData.fpControl = { type: "footswitch", key: fs.key };
  interactiveMeshes.push(fs.pickMesh);
});

let activeKnobKey = null;
let activeFootswitchKey = null;
let activePointerId = null;
let activeKnobStartY = 0;
let activeKnobStartValue = 0;

function valueFromKnobDelta(startValue, deltaY) {
  return clamp01(startValue + deltaY / 220);
}

function screenToNDC(clientX, clientY) {
  const rect = renderer.domElement.getBoundingClientRect();
  const x = ((clientX - rect.left) / rect.width) * 2 - 1;
  const y = -((clientY - rect.top) / rect.height) * 2 + 1;
  pointer.set(x, y);
}

function pickControl(clientX, clientY) {
  screenToNDC(clientX, clientY);
  raycaster.setFromCamera(pointer, camera);
  const hits = raycaster.intersectObjects(interactiveMeshes, true);
  for (const hit of hits) {
    const control = hit.object.userData.fpControl;
    if (control) return control;
  }
  return null;
}

function safeSetPointerCapture(pointerId) {
  if (pointerId == null) return;
  if (typeof renderer.domElement.setPointerCapture !== "function") return;
  try {
    renderer.domElement.setPointerCapture(pointerId);
  } catch { }
}

function safeReleasePointerCapture(pointerId) {
  if (pointerId == null) return;
  if (typeof renderer.domElement.releasePointerCapture !== "function") return;
  try {
    renderer.domElement.releasePointerCapture(pointerId);
  } catch { }
}

function handlePress(clientX, clientY, pointerId, rawEvent) {
  const control = pickControl(clientX, clientY);
  if (!control) {
    debugLog(`press miss pid=${pointerId ?? "na"} x=${clientX.toFixed?.(1) ?? clientX} y=${clientY.toFixed?.(1) ?? clientY}`);
    return false;
  }
  if (rawEvent) {
    rawEvent.preventDefault?.();
    rawEvent.stopPropagation?.();
  }

  debugLog(`press hit ${control.type}:${control.key} pid=${pointerId ?? "na"}`);
  activePointerId = pointerId;

  if (control.type === "knob") {
    activeKnobKey = control.key;
    activeKnobStartY = clientY;
    activeKnobStartValue = Number(state[activeKnobKey] ?? 0);
    controls.enabled = false;
    safeSetPointerCapture(pointerId);
    return true;
  }

  if (control.type === "toggle") {
    setState({ [control.key]: !Boolean(state[control.key]) });
    debugLog(`toggle ${control.key}`);
    emitControl({ type: "toggle", key: control.key });
    controls.enabled = true;
    activePointerId = null;
    return true;
  }

  controls.enabled = false;
  safeSetPointerCapture(pointerId);

  if (control.type === "footswitch" && control.key === "freeze") {
    activeFootswitchKey = "freeze";
    setState({ freezePressed: true });
    debugLog("freeze down");
    emitControl({ type: "freeze", pressed: true });
    return true;
  }

  if (control.type === "footswitch" && control.key === "bypass") {
    activeFootswitchKey = "bypass";
    setState({ bypassPressed: true, bypassEnabled: !Boolean(state.bypassEnabled) });
    debugLog("bypass down");
    emitControl({ type: "bypass", pressed: true });
    return true;
  }

  return false;
}

function handleMove(clientY, pointerId) {
  if (activePointerId !== null && pointerId !== activePointerId) return false;
  if (!activeKnobKey) return false;
  const deltaY = activeKnobStartY - clientY;
  const value = valueFromKnobDelta(activeKnobStartValue, deltaY);
  setState({ [activeKnobKey]: value });
  emitControl({ type: "knob", key: activeKnobKey, value });
  return true;
}

function handleRelease(pointerId, rawEvent) {
  if (activePointerId !== null && pointerId !== activePointerId) return false;
  let handled = false;
  if (activeKnobKey) {
    activeKnobKey = null;
    handled = true;
  }

  if (activeFootswitchKey) {
    if (activeFootswitchKey === "freeze") {
      setState({ freezePressed: false });
      debugLog("freeze up");
      emitControl({ type: "freeze", pressed: false });
    } else if (activeFootswitchKey === "bypass") {
      setState({ bypassPressed: false });
      debugLog("bypass up");
      emitControl({ type: "bypass", pressed: false });
    }
    activeFootswitchKey = null;
    handled = true;
  }

  if (handled) {
    controls.enabled = true;
    safeReleasePointerCapture(pointerId);
    activePointerId = null;
    rawEvent?.preventDefault?.();
  }
  return handled;
}

function handleCancel(pointerId) {
  if (activePointerId !== null && pointerId !== activePointerId) return false;
  if (activeKnobKey) {
    activeKnobKey = null;
  }
  if (activeFootswitchKey === "freeze") {
    setState({ freezePressed: false });
    debugLog("freeze cancel");
    emitControl({ type: "freeze", pressed: false });
  } else if (activeFootswitchKey === "bypass") {
    setState({ bypassPressed: false });
    debugLog("bypass cancel");
    emitControl({ type: "bypass", pressed: false });
  }
  activeFootswitchKey = null;
  controls.enabled = true;
  safeReleasePointerCapture(pointerId);
  activePointerId = null;
  return true;
}

renderer.domElement.addEventListener("pointerdown", (event) => {
  controls.autoRotate = false;
  debugLog(`evt pointerdown pid=${event.pointerId} type=${event.pointerType}`);
  handlePress(event.clientX, event.clientY, event.pointerId, event);
}, true);

renderer.domElement.addEventListener("pointermove", (event) => {
  handleMove(event.clientY, event.pointerId);
});

renderer.domElement.addEventListener("pointerup", (event) => {
  debugLog(`evt pointerup pid=${event.pointerId} type=${event.pointerType}`);
  handleRelease(event.pointerId, event);
});

renderer.domElement.addEventListener("pointercancel", (event) => {
  debugLog(`evt pointercancel pid=${event.pointerId} type=${event.pointerType}`);
  handleCancel(event.pointerId);
});

if (!window.PointerEvent) {
  debugLog("PointerEvent unavailable, using mouse fallback");
  renderer.domElement.addEventListener("mousedown", (event) => {
    debugLog("evt mousedown");
    handlePress(event.clientX, event.clientY, 1, event);
  }, true);
  window.addEventListener("mousemove", (event) => {
    handleMove(event.clientY, 1);
  });
  window.addEventListener("mouseup", (event) => {
    debugLog("evt mouseup");
    handleRelease(1, event);
  });
}

renderer.domElement.addEventListener("wheel", (event) => {
  controls.autoRotate = false;
  const control = pickControl(event.clientX, event.clientY);
  if (!control || control.type !== "knob") return;
  const step = event.deltaY < 0 ? 0.02 : -0.02;
  const value = clamp01(Number(state[control.key] ?? 0) + step);
  setState({ [control.key]: value });
  emitControl({ type: "knob", key: control.key, value });
  event.preventDefault();
}, { passive: false });

function resize() {
  const width = mount.clientWidth;
  const height = mount.clientHeight;
  if (width <= 0 || height <= 0) return;
  renderer.setSize(width, height, false);
  camera.aspect = width / height;
  camera.updateProjectionMatrix();
}

window.addEventListener("resize", resize);
resize();
applyVisualState();
debugLog(`debug=${debugEnabled ? "on" : "off"} controls=${interactiveMeshes.length} pointer=${!!window.PointerEvent}`);

function render() {
  updateDebugStatus(performance.now());
  controls.update();
  renderer.render(scene, camera);
  requestAnimationFrame(render);
}
render();
